/**
 * lib/cover.ts
 * ------------------------------------------------------------------
 * Cover for an absent teacher (the school, 2026-10-28). Every rule of the
 * Cover screen, pure and tested (cover.test.ts); the screen only draws it.
 *
 * WHO IS OFFERED, AND IN WHAT ORDER (the school's words):
 *   1. another teacher of THAT SUBJECT,
 *   2. then a teacher of THAT CLASS,
 *   3. then anyone else in the SAME SECTION (primary or secondary).
 *   Inside each group: the LIGHTEST DAY first (lessons, exam duties and covers
 *   already given that day), then the FEWEST COVERS THIS YEAR, then surname.
 *
 * Only teachers free for the whole lesson are offered. Everyone else (teaching,
 * on exam duty, already covering, away, or in the other part of the school) is
 * kept in a second list with the reason, because the school's rule is that on
 * a day with many staff out "we may need to throw all rules away": the manager
 * may give a cover to anybody, and the screen says what it costs. There is no
 * daily limit and a teacher cannot refuse a cover.
 *
 * TIMES, NOT PERIOD NUMBERS. Primary runs thirteen periods and secondary
 * eleven, on different bells, so "period 4" means two different moments.
 * Every lesson, duty and cover is turned into minutes from midnight on its
 * own bell before anything is compared.
 *
 * A COVER IS ONE LESSON. A double lesson is one cover, not two. The yearly
 * count is DERIVED from the cover records, never stored, so taking a cover
 * back takes it off the count.
 * ------------------------------------------------------------------ */
import {
  dayForDate, groupsForTeacher, isSecondaryClassName, slotTeacherIds,
  type Day, type ScheduleSettings, type SlotGroup, type TeacherPhase, type TimetableSlot,
} from '@/lib/timetable';
import { parseRange, tidySubjectLabel } from '@/lib/liveTimetable';

export const COVERS = 'reportCovers';
export const COVER_ABSENCES = 'reportCoverAbsences';

export type SchoolSection = 'primary' | 'secondary';

/** One lesson covered by one teacher. Doc id: coverId(). */
export interface CoverRecord {
  id: string;
  /** YYYY-MM-DD, Gaborone. */
  date: string;
  year: number;
  day: Day;
  absentTeacherId: string;
  absentTeacherName: string;
  coverTeacherId: string;
  coverTeacherName: string;
  slotId: string;
  periodStart: number;
  periodEnd: number;
  /** "07:45", on the lesson's own bell. */
  from: string;
  to: string;
  classes: string[];
  subject: string;
  room: string;
  section: SchoolSection;
  issuedBy: string;
  issuedByName: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Who is away on a day, and why. Cover managers only (the reason is private). */
export interface CoverAbsence {
  id: string;
  date: string;
  year: number;
  teacherId: string;
  reason: string;
  createdBy: string;
  createdAt?: string;
}

export const ABSENCE_REASONS = ['Sick', 'Leave', 'Workshop or school business', 'Family matter', 'Other'] as const;

export const coverId = (date: string, absentTeacherId: string, slotId: string) => `${date}__${absentTeacherId}__${slotId}`;
export const absenceId = (date: string, teacherId: string) => `${date}__${teacherId}`;

/** The timetable day a date falls on — LKC's CYCLE day (School Settings →
 *  Teaching Days) — or null on a day without teaching or for a bad date. The
 *  name is Al-Nur's, kept so its callers port unchanged. */
export function weekdayOf(ymd: string): Day | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const d = new Date(`${ymd}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return dayForDate(ymd);
}

export const hm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const toMin = (t: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(t.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

export const sectionOfClasses = (classes: string[]): SchoolSection =>
  classes.some(isSecondaryClassName) ? 'secondary' : 'primary';

/**
 * A slot's start and end on its OWN bell, in minutes. `scheduleOf` picks the
 * bell for a class (primary or secondary, from the class's division).
 */
export function slotMinutes(
  slot: TimetableSlot,
  scheduleOf: (cls: string) => ScheduleSettings | null,
): [number, number] | null {
  const schedule = scheduleOf(slot.classes?.[0] ?? '');
  if (!schedule) return null;
  const bell = schedule.days[slot.day] ?? [];
  const first = bell.find((p) => p.period === slot.period_start);
  const last = bell.find((p) => p.period === slot.period_end) ?? first;
  const a = first ? parseRange(first.time) : null;
  const b = last ? parseRange(last.time) : null;
  return a && b ? [a[0], b[1]] : null;
}

/** What a subject is called for matching: the subject id, else its tidy label. */
export function subjectKey(g: Pick<SlotGroup, 'subjectId' | 'subjectLabel' | 'streamLabel'>): string {
  if (g.subjectId) return `id:${g.subjectId}`;
  const label = tidySubjectLabel(g.subjectLabel, g.streamLabel);
  return label ? `label:${label.toLowerCase()}` : '';
}

/** A lesson of the absent teacher that needs covering. */
export interface CoverLesson {
  slotId: string;
  day: Day;
  periodStart: number;
  periodEnd: number;
  from: number;
  to: number;
  classes: string[];
  subject: string;
  subjectKeys: string[];
  room: string;
  section: SchoolSection;
  /** Others teaching the same group, who may make cover unnecessary. */
  coTeacherIds: string[];
}

/** The absent teacher's lessons on one day, in time order (their own group of an option block). */
export function lessonsToCover(
  teacherId: string,
  day: Day,
  slots: TimetableSlot[],
  scheduleOf: (cls: string) => ScheduleSettings | null,
  subjectOf: (g: SlotGroup) => string | undefined,
): CoverLesson[] {
  const out: CoverLesson[] = [];
  for (const slot of slots) {
    if (slot.day !== day || !slotTeacherIds(slot).includes(teacherId)) continue;
    const r = slotMinutes(slot, scheduleOf);
    if (!r) continue;
    const { groups } = groupsForTeacher(slot, teacherId);
    const classes = [...new Set(groups.flatMap((g) => (g.classes?.length ? g.classes : slot.classes ?? [])))];
    const co = new Set<string>();
    groups.forEach((g) => (g.teacherIds ?? []).forEach((t) => { if (t !== teacherId) co.add(t); }));
    out.push({
      slotId: slot.id,
      day,
      periodStart: slot.period_start,
      periodEnd: slot.period_end,
      from: r[0],
      to: r[1],
      classes,
      subject: [...new Set(groups.map((g) => subjectOf(g) ?? '').filter(Boolean))].join(' / ') || 'Lesson',
      subjectKeys: [...new Set(groups.map(subjectKey).filter(Boolean))],
      room: [...new Set(groups.map((g) => g.room).filter(Boolean))].join(', '),
      section: sectionOfClasses(classes),
      coTeacherIds: [...co],
    });
  }
  return out.sort((a, b) => a.from - b.from);
}

/* ============================ a teacher's day ============================ */

export interface Busy {
  from: number;
  to: number;
  kind: 'lesson' | 'examDuty' | 'cover';
  /** For a cover: its id, so a cover being re-given is not counted against its own holder. */
  coverId?: string;
}

/** An exam duty reduced to what availability needs. */
export interface DutyWindow { teacherId: string; from: string; to: string }

/**
 * Everything one teacher is doing on a date: their lessons on that weekday,
 * their exam duties that date, and the covers they hold that date.
 */
export function busyFor(
  teacherId: string,
  day: Day,
  slots: TimetableSlot[],
  scheduleOf: (cls: string) => ScheduleSettings | null,
  duties: DutyWindow[],
  covers: Pick<CoverRecord, 'id' | 'coverTeacherId' | 'from' | 'to'>[],
): Busy[] {
  const out: Busy[] = [];
  for (const slot of slots) {
    if (slot.day !== day || !slotTeacherIds(slot).includes(teacherId)) continue;
    const r = slotMinutes(slot, scheduleOf);
    if (r) out.push({ from: r[0], to: r[1], kind: 'lesson' });
  }
  for (const d of duties) {
    if (d.teacherId !== teacherId) continue;
    const a = toMin(d.from), b = toMin(d.to);
    if (a != null && b != null && b > a) out.push({ from: a, to: b, kind: 'examDuty' });
  }
  for (const c of covers) {
    if (c.coverTeacherId !== teacherId) continue;
    const a = toMin(c.from), b = toMin(c.to);
    if (a != null && b != null && b > a) out.push({ from: a, to: b, kind: 'cover', coverId: c.id });
  }
  return out;
}

/** Minutes actually occupied: overlapping items are counted once. */
export function busyMinutes(busy: Busy[]): number {
  const xs = busy.map((b) => [b.from, b.to] as [number, number]).sort((a, b) => a[0] - b[0]);
  let total = 0, end = -1, start = -1;
  for (const [a, b] of xs) {
    if (a > end) { if (end > start) total += end - start; start = a; end = b; }
    else end = Math.max(end, b);
  }
  if (end > start) total += end - start;
  return total;
}

/** Touching is not overlapping: 08:15–08:40 and 08:40–09:05 are back to back. */
const overlaps = (a: { from: number; to: number }, b: { from: number; to: number }) => a.from < b.to && b.from < a.to;

/* ============================ ranking ============================ */

export type CoverTier = 'subject' | 'class' | 'section';
export type CoverBlock = 'away' | 'teaching' | 'examDuty' | 'covering' | 'otherSection';

export const TIER_LABEL: Record<CoverTier, string> = {
  subject: 'Teaches this subject',
  class: 'Teaches this class',
  section: 'Same section',
};
export const BLOCK_LABEL: Record<CoverBlock, string> = {
  away: 'away today',
  teaching: 'teaching then',
  examDuty: 'on exam duty then',
  covering: 'already covering then',
  otherSection: 'other part of the school',
};

/** What the ranking needs to know about one teacher, worked out by the caller. */
export interface CoverTeacher {
  id: string;
  surname: string;
  phases: Set<TeacherPhase>;
  /** subjectKey()s this teacher teaches, from the timetable. */
  subjects: Set<string>;
  /** Classes this teacher teaches, from the timetable. */
  classes: Set<string>;
  busy: Busy[];
  away: boolean;
  coversThisYear: number;
}

export interface CoverCandidate {
  teacherId: string;
  tier: CoverTier;
  blocks: CoverBlock[];
  /** Occupied minutes that day: the first sort key inside a tier. */
  dayMinutes: number;
  lessons: number;
  examDuties: number;
  covers: number;
  coversThisYear: number;
}

const TIER_RANK: Record<CoverTier, number> = { subject: 0, class: 1, section: 2 };

/**
 * Rank everyone for one lesson. `free` is the list a manager picks from;
 * `busy` is everyone else, ordered the same way, each with what stops them.
 * `ignoreCoverId` is the cover being given to someone else: its present
 * holder is not "already covering" their own lesson.
 */
export function rankCandidates(
  lesson: CoverLesson,
  absentTeacherId: string,
  teachers: CoverTeacher[],
  ignoreCoverId?: string,
): { free: CoverCandidate[]; busy: CoverCandidate[] } {
  const rows: (CoverCandidate & { surname: string })[] = [];
  for (const t of teachers) {
    if (t.id === absentTeacherId) continue;
    const busy = t.busy.filter((b) => !(ignoreCoverId && b.coverId === ignoreCoverId));
    const tier: CoverTier = lesson.subjectKeys.some((k) => t.subjects.has(k)) ? 'subject'
      : lesson.classes.some((c) => t.classes.has(c)) ? 'class' : 'section';
    const blocks: CoverBlock[] = [];
    if (t.away) blocks.push('away');
    const clash = busy.filter((b) => overlaps(b, lesson));
    if (clash.some((b) => b.kind === 'lesson')) blocks.push('teaching');
    if (clash.some((b) => b.kind === 'examDuty')) blocks.push('examDuty');
    if (clash.some((b) => b.kind === 'cover')) blocks.push('covering');
    if (!t.phases.has(lesson.section)) blocks.push('otherSection');
    rows.push({
      teacherId: t.id,
      surname: t.surname,
      tier,
      blocks,
      dayMinutes: busyMinutes(busy),
      lessons: busy.filter((b) => b.kind === 'lesson').length,
      examDuties: busy.filter((b) => b.kind === 'examDuty').length,
      covers: busy.filter((b) => b.kind === 'cover').length,
      coversThisYear: t.coversThisYear,
    });
  }
  rows.sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier]
    || a.dayMinutes - b.dayMinutes
    || a.coversThisYear - b.coversThisYear
    || a.surname.localeCompare(b.surname, undefined, { sensitivity: 'base' }));
  const strip = ({ surname: _s, ...c }: CoverCandidate & { surname: string }): CoverCandidate => c;
  return {
    free: rows.filter((r) => !r.blocks.length).map(strip),
    busy: rows.filter((r) => r.blocks.length).map(strip),
  };
}

/* ============================ the year's count ============================ */

export interface CoverTally { given: number; away: number }

/** Covers given and lessons covered while away, per teacher, for one year. */
export function coverTally(covers: Pick<CoverRecord, 'year' | 'coverTeacherId' | 'absentTeacherId'>[], year: number): Map<string, CoverTally> {
  const out = new Map<string, CoverTally>();
  const row = (id: string) => { if (!out.has(id)) out.set(id, { given: 0, away: 0 }); return out.get(id)!; };
  for (const c of covers) {
    if (c.year !== year) continue;
    row(c.coverTeacherId).given += 1;
    row(c.absentTeacherId).away += 1;
  }
  return out;
}

/** A teacher's covers on one date, as minutes, for My day and School now. */
export function coversFor(teacherId: string, ymd: string, covers: CoverRecord[]): (CoverRecord & { fromMin: number; toMin: number })[] {
  return covers
    .filter((c) => c.coverTeacherId === teacherId && c.date === ymd)
    .map((c) => ({ ...c, fromMin: toMin(c.from) ?? 0, toMin: toMin(c.to) ?? 0 }))
    .sort((a, b) => a.fromMin - b.fromMin);
}
